import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui";
import { ProductCard } from "@/components/atoms";
import { Heart, Share, Check, Star, Minus, Plus, ArrowLeft, ArrowRight } from "lucide-react";
import { useGetProductById, useGetRelatedProducts } from "@/api/queries";
import { formatPrice, getProductAvailability } from "@/lib/utils";
import { Spinner } from "@/assets/icons";
import { useAddToCart } from "@/api/mutation";
import { useUser } from "@/providers/user";

type ProductOption = {
  name: string;
  values: string[];
};

export const ProductDetails = () => {
  const { productSlug } = useParams<{ productSlug: string }>();
  const navigate = useNavigate();

  const { user } = useUser("product-details");
  const isAuthenticated = !!user;

  const productId = Number(productSlug?.split("-")[0]);

  const { data: singleProduct, isFetching: isProductFetching } = useGetProductById(productId);
  const product = singleProduct?.data;

  const { data: relatedProductsData, isFetching: isRelatedFetching } =
    useGetRelatedProducts(productId);
  const relatedProducts = relatedProductsData?.data;

  const addToCartMutation = useAddToCart();

  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  // Zoom states
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [isPinching, setIsPinching] = useState(false);
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const [lastTap, setLastTap] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);

  if (isProductFetching) {
    return (
      <div className='min-h-screen flex flex-col items-center justify-center'>
        <Spinner />
        <p className='text-gray-500 mt-4'>Loading product details...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className='min-h-screen flex flex-col items-center justify-center text-center px-4'>
        <h2 className='text-2xl font-bold text-gray-800 mb-4'>Product Not Found</h2>
        <p className='text-gray-600 mb-8'>We couldn't find the product you're looking for.</p>
        <Button onClick={() => navigate("/shop")}>Return to Shop</Button>
      </div>
    );
  }

  const allProductImages = product.images?.map((img) => img.url) || [];

  const sizeValues = product.variants?.map((v) => v.value) || [];
  const optionsArray: ProductOption[] =
    sizeValues.length > 0 ? [{ name: "Size", values: sizeValues }] : [];

  const getSelectedVariant = () => {
    if (!product.variants || product.variants.length === 0) {
      return null;
    }

    const selectedSize = selectedOptions["Size"];
    if (!selectedSize) {
      // Prefer a variant with stock if available
      const inStockVariant = product.variants.find((v) => v.stock !== 0);
      return inStockVariant || product.variants[0];
    }

    return (
      product.variants.find((variant) => variant.value === selectedSize) || product.variants[0]
    );
  };

  const areAllOptionsSelected = () => {
    if (optionsArray.length === 0) return true;
    return optionsArray.every((option) => selectedOptions[option.name]);
  };

  // Helper: interpret variant stock (null = unlimited)
  const getVariantStockInfo = (variant: (typeof product.variants)[0] | null) => {
    if (!variant) {
      return { isInfinite: false, available: 0 };
    }

    if (variant.stock === null) {
      return { isInfinite: true, available: Infinity };
    }

    return { isInfinite: false, available: Math.max(0, variant.stock) };
  };

  const selectedVariant = getSelectedVariant();
  const { isInfinite, available: variantAvailableStock } = getVariantStockInfo(selectedVariant);

  // Max quantity for UX (even if unlimited)
  const MAX_QUANTITY = 99;

  const decreaseQuantity = () => {
    if (quantity > 1) setQuantity(quantity - 1);
  };

  const increaseQuantity = () => {
    if (isInfinite) {
      if (quantity < MAX_QUANTITY) {
        setQuantity(quantity + 1);
      }
    } else if (quantity < variantAvailableStock) {
      setQuantity(quantity + 1);
    }
  };

  const handleOptionChange = (optionName: string, value: string) => {
    setSelectedOptions({
      ...selectedOptions,
      [optionName]: value,
    });
    // Reset quantity when variant changes
    setQuantity(1);
  };

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      navigate("/login", {
        state: { from: window.location.pathname },
        replace: false,
      });
      return;
    }

    const currentSelectedVariant = getSelectedVariant();

    if (!currentSelectedVariant) {
      console.error("No variant selected");
      return;
    }

    if (!areAllOptionsSelected()) {
      console.error("Please select all options");
      return;
    }

    // Prevent adding out-of-stock items
    if (currentSelectedVariant.stock === 0) {
      console.error("Selected variant is out of stock");
      return;
    }

    const cartPayload = {
      productId: product.id,
      variantId: currentSelectedVariant.id.toString(),
      quantity,
    };

    addToCartMutation.mutate(cartPayload);
  };

  const nextImage = () => {
    setActiveImage((activeImage + 1) % allProductImages.length);
    // Reset zoom when changing images
    setZoomLevel(1);
    setZoomPosition({ x: 50, y: 50 });
  };

  const prevImage = () => {
    setActiveImage((activeImage - 1 + allProductImages.length) % allProductImages.length);
    // Reset zoom when changing images
    setZoomLevel(1);
    setZoomPosition({ x: 50, y: 50 });
  };

  // Zoom handlers
  const getTouchDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (touches: React.TouchList, rect: DOMRect) => {
    const x = (((touches[0].clientX + touches[1].clientX) / 2 - rect.left) / rect.width) * 100;
    const y = (((touches[0].clientY + touches[1].clientY) / 2 - rect.top) / rect.height) * 100;
    return { x, y };
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 2) {
      // Only prevent default when pinching (2 fingers)
      e.preventDefault();
      e.stopPropagation();

      setIsPinching(true);
      setLastTouchDistance(getTouchDistance(e.touches));
      const rect = e.currentTarget.getBoundingClientRect();
      const center = getTouchCenter(e.touches, rect);
      setZoomPosition(center);
    } else if (e.touches.length === 1) {
      // Handle double tap
      const now = Date.now();
      const DOUBLE_TAP_DELAY = 300;

      if (now - lastTap < DOUBLE_TAP_DELAY) {
        e.preventDefault();

        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.touches[0].clientX - rect.left) / rect.width) * 100;
        const y = ((e.touches[0].clientY - rect.top) / rect.height) * 100;

        if (zoomLevel === 1) {
          setZoomLevel(2.5);
          setZoomPosition({ x, y });
        } else {
          setZoomLevel(1);
          setZoomPosition({ x: 50, y: 50 });
        }
        setLastTap(0);
      } else {
        setLastTap(now);
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 2 && isPinching) {
      // Prevent scrolling only when actively pinching
      e.preventDefault();
      e.stopPropagation();

      const currentDistance = getTouchDistance(e.touches);
      const delta = (currentDistance - lastTouchDistance) * 0.01;
      const newZoom = Math.min(Math.max(1, zoomLevel + delta), 3);

      setZoomLevel(newZoom);
      setLastTouchDistance(currentDistance);

      if (newZoom === 1) {
        setZoomPosition({ x: 50, y: 50 });
      }
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      // Prevent scrolling only when zoomed and panning
      e.preventDefault();
      e.stopPropagation();

      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.touches[0].clientX - rect.left) / rect.width) * 100;
      const y = ((e.touches[0].clientY - rect.top) / rect.height) * 100;
      setZoomPosition({ x, y });
    }
    // Single touch when not zoomed - allow normal page scrolling
  };

  const handleTouchEnd = () => {
    setIsPinching(false);
    setLastTouchDistance(0);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoomLevel === 1) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setZoomPosition({ x, y });
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();

    const delta = e.deltaY * -0.01;
    const newZoom = Math.min(Math.max(1, zoomLevel + delta), 3);

    setZoomLevel(newZoom);

    // Reset position when zooming out completely
    if (newZoom === 1) {
      setZoomPosition({ x: 50, y: 50 });
    }
  };

  const handleImageClick = () => {
    // Only toggle zoom on desktop (prevent conflict with mobile gestures)
    if (window.innerWidth >= 768) {
      if (zoomLevel === 1) {
        setZoomLevel(2);
      } else {
        setZoomLevel(1);
        setZoomPosition({ x: 50, y: 50 });
      }
    }
  };

  const displayPrice = selectedVariant ? selectedVariant.price : product.finalPrice;

  // Determine availability message
  const renderAvailability = () => {
    // If product has variants, use selected variant
    if (product.variants && product.variants.length > 0) {
      if (!selectedVariant) {
        return <span className='text-gray-500'>Select an option to check availability</span>;
      }

      if (selectedVariant.stock === null) {
        return (
          <>
            <Check size={18} className='text-green-500' />
            <span className='text-green-500 font-medium'>In Stock</span>
            <span className='text-gray-500'>(Unlimited)</span>
          </>
        );
      } else if (selectedVariant.stock > 0) {
        return (
          <>
            <Check size={18} className='text-green-500' />
            <span className='text-green-500 font-medium'>In Stock</span>
            <span className='text-gray-500'>({selectedVariant.stock} units)</span>
          </>
        );
      } else {
        return <span className='text-red-500'>Out of Stock</span>;
      }
    }

    // Non-variant product
    if (product.totalStock === null) {
      return (
        <>
          <Check size={18} className='text-green-500' />
          <span className='text-green-500 font-medium'>In Stock</span>
          <span className='text-gray-500'>(Unlimited)</span>
        </>
      );
    } else if (product.totalStock > 0) {
      return (
        <>
          <Check size={18} className='text-green-500' />
          <span className='text-green-500 font-medium'>In Stock</span>
          <span className='text-gray-500'>({product.totalStock} units)</span>
        </>
      );
    } else {
      return <span className='text-red-500'>Out of Stock</span>;
    }
  };

  return (
    <div className='bg-white'>
      {/* Breadcrumb */}
      <nav className='container mx-auto py-4 px-4 text-sm'>
        <ol className='flex items-center space-x-1'>
          <li>
            <Link to='/' className='text-gray-500 hover:text-primary'>
              Home
            </Link>
          </li>
          <li className='text-gray-500'>/</li>
          <li>
            <Link to='/shop' className='text-gray-500 hover:text-primary'>
              Shop
            </Link>
          </li>
          <li className='text-gray-500'>/</li>
          <li className='text-gray-800 font-medium truncate'>{product.name}</li>
        </ol>
      </nav>

      {/* Product Section */}
      <section className='container mx-auto px-4 py-8'>
        <div className='flex flex-col lg:flex-row lg:space-x-12'>
          {/* Product Images */}
          <div className='lg:w-1/2 mb-10 lg:mb-0'>
            <div className='relative rounded-lg overflow-hidden bg-gray-100 h-[400px] mb-4'>
              <div
                className={`w-full h-full overflow-hidden select-none ${
                  zoomLevel > 1 ? "cursor-move" : "md:cursor-zoom-in"
                }`}
                onMouseMove={handleMouseMove}
                onWheel={handleWheel}
                onClick={handleImageClick}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{
                  touchAction: zoomLevel > 1 ? "none" : "pan-y",
                  WebkitUserSelect: "none",
                  userSelect: "none",
                }}
              >
                <img
                  src={allProductImages[activeImage]}
                  alt={`${product.name} - view ${activeImage + 1}`}
                  className='w-full h-full object-cover md:object-contain transition-transform duration-200 ease-out pointer-events-none'
                  style={{
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                  }}
                  draggable={false}
                />
              </div>

              {allProductImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className='absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition-all'
                    aria-label='Previous image'
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <button
                    onClick={nextImage}
                    className='absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 p-2 rounded-full shadow-md transition-all'
                    aria-label='Next image'
                  >
                    <ArrowRight size={20} />
                  </button>
                </>
              )}

              {/* Zoom Indicator */}
              {zoomLevel > 1 && (
                <div className='absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium'>
                  {Math.round(zoomLevel * 100)}%
                </div>
              )}
            </div>

            {allProductImages.length > 1 && (
              <div className='flex space-x-4 overflow-x-auto pb-2'>
                {allProductImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setActiveImage(index);
                      setZoomLevel(1);
                      setZoomPosition({ x: 50, y: 50 });
                    }}
                    className={`shrink-0 w-24 h-24 rounded-md overflow-hidden border-2 ${
                      activeImage === index
                        ? "border-primary"
                        : "border-transparent hover:border-gray-300"
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} thumbnail ${index + 1}`}
                      className='w-full h-full object-cover'
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className='lg:w-1/2'>
            <div className='mb-6'>
              <h1 className='text-3xl font-bold text-gray-900 mb-2'>{product.name}</h1>

              <div className='flex items-center space-x-4 mb-4'>
                <div className='flex'>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={18}
                      className={
                        star <= (product.rating || 0)
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-gray-300"
                      }
                    />
                  ))}
                </div>
                <span className='text-gray-500'>
                  {product.rating || 0} ({product.reviewCount} reviews)
                </span>
              </div>
              <p className='text-gray-600 mb-6'>SKU: {product.sku}</p>

              <p className='text-gray-600 mb-6'>{product.description}</p>

              <div className='flex items-center space-x-4 mb-6'>
                <span className='text-3xl font-bold text-gray-900'>
                  {formatPrice(parseInt(displayPrice))}
                </span>
                {product.basePrice && product.basePrice !== product.finalPrice && (
                  <>
                    <span className='text-lg text-gray-500 line-through'>
                      {formatPrice(parseInt(product.basePrice))}
                    </span>
                    <span className='px-2 py-1 bg-red-100 text-red-700 text-sm font-medium rounded'>
                      {product.discountPercentage}% OFF
                    </span>
                  </>
                )}
              </div>

              {/* Availability — fully updated */}
              <div className='flex items-center space-x-2 text-sm mb-8'>{renderAvailability()}</div>
            </div>

            {/* Size Selector */}
            {optionsArray.length > 0 && (
              <div className='mb-8 space-y-4'>
                {optionsArray.map((option) => (
                  <div key={option.name}>
                    <h3 className='text-sm font-medium text-gray-900 mb-2 capitalize'>
                      {option.name}{" "}
                      {!selectedOptions[option.name] && <span className='text-red-500'>*</span>}
                    </h3>
                    <div className='flex flex-wrap gap-3'>
                      {option.values.map((value) => {
                        const variant = product.variants?.find((v) => v.value === value);
                        const isOutOfStock = variant?.stock === 0;

                        return (
                          <button
                            key={value}
                            onClick={() => !isOutOfStock && handleOptionChange(option.name, value)}
                            disabled={isOutOfStock}
                            className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                              isOutOfStock
                                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                : selectedOptions[option.name] === value
                                ? "bg-primary text-white border-primary"
                                : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {value}
                            {isOutOfStock && <span className='ml-1 text-xs'>(Out of Stock)</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {!areAllOptionsSelected() && (
                  <p className='text-sm text-red-500'>Please select a size before adding to cart</p>
                )}
              </div>
            )}

            {/* Specifications */}
            {product.specifications && product.specifications.length > 0 && (
              <div className='mb-8'>
                <h3 className='text-lg font-medium text-gray-900 mb-3'>Specifications</h3>
                <div className='bg-gray-50 rounded-lg p-4'>
                  <dl className='divide-y divide-gray-200'>
                    {product.specifications.map((spec) => (
                      <div key={spec.id} className='py-3 flex justify-between'>
                        <dt className='text-sm font-medium text-gray-500 capitalize'>
                          {spec.name}
                        </dt>
                        <dd className='text-sm text-gray-900 text-right'>{spec.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            )}

            {/* Add to Cart Controls */}
            <div className='space-y-6'>
              <div className='flex items-center'>
                <span className='mr-4 text-gray-700 font-medium'>Quantity:</span>
                <div className='flex items-center border border-gray-300 rounded-md'>
                  <button
                    onClick={decreaseQuantity}
                    disabled={quantity <= 1}
                    className='px-3 py-2 text-gray-600 hover:text-gray-700 disabled:text-gray-400'
                  >
                    <Minus size={18} />
                  </button>
                  <span className='px-4 py-2 border-x border-gray-300 w-16 text-center'>
                    {quantity}
                  </span>
                  <button
                    onClick={increaseQuantity}
                    disabled={
                      (!isInfinite && quantity >= variantAvailableStock) ||
                      (isInfinite && quantity >= MAX_QUANTITY)
                    }
                    className='px-3 py-2 text-gray-600 hover:text-gray-700 disabled:text-gray-400'
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              <div className='flex flex-col sm:flex-row gap-4'>
                <Button
                  onClick={handleAddToCart}
                  disabled={
                    (product.variants && product.variants.length > 0
                      ? !selectedVariant || selectedVariant.stock === 0
                      : product.totalStock === 0) ||
                    !areAllOptionsSelected() ||
                    addToCartMutation.isPending
                  }
                  className='flex-1 py-3'
                  size='lg'
                >
                  {addToCartMutation.isPending ? "Adding..." : "Add to Cart"}
                </Button>
                <Button
                  variant='outline'
                  className='flex-1 py-3 border-gray-300 text-gray-700 hover:bg-gray-50'
                  size='lg'
                >
                  <Heart size={20} className='mr-2' />
                  Add to Wishlist
                </Button>
              </div>

              <button className='flex items-center text-gray-500 hover:text-primary'>
                <Share size={18} className='mr-2' />
                <span>Share this Product</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className='bg-gray-50 py-12'>
        <div className='container mx-auto px-4'>
          <h2 className='text-2xl font-bold text-gray-900 mb-8'>Related Products</h2>

          {isRelatedFetching ? (
            <div className='text-center py-8'>
              <p className='text-gray-500'>Loading related products...</p>
            </div>
          ) : relatedProducts && relatedProducts.length > 0 ? (
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'>
              {relatedProducts.map((relatedProduct) => {
                const { hasStock, isUnlimited } = getProductAvailability(product);

                return (
                  <ProductCard
                    key={relatedProduct.id}
                    id={relatedProduct.id}
                    name={relatedProduct.name}
                    image={relatedProduct.images[0]?.url || ""}
                    price={relatedProduct.finalPrice}
                    oldPrice={relatedProduct.basePrice}
                    hasStock={hasStock}
                    isUnlimited={isUnlimited}
                    sku={relatedProduct.sku}
                    discount={relatedProduct.discountPercentage}
                    onAddToCart={() =>
                      navigate(`/product/${relatedProduct.id}-${relatedProduct.sku}`)
                    }
                  />
                );
              })}
            </div>
          ) : (
            <p className='text-gray-500 text-center py-8'>No related products found</p>
          )}
        </div>
      </section>
    </div>
  );
};
